import { PITCH } from '../game/camera/CameraController';

/**
 * Kompas w rogu ekranu: gdzie jest północ.
 *
 * HTML over the canvas, like the name tags and the status line, and for the
 * same reason: the game draws into a buffer scaled up whole, and a letter drawn
 * in there would be a smear. This is interface, not world.
 *
 * # Which way the needle points
 *
 * At the **north on screen**, not at the yaw of the camera — and those are two
 * different angles. The camera looks down at 35,264°, so a map direction is
 * foreshortened on its way to the picture: heading straight north takes you up
 * the screen at 0,577 of the rate that heading east takes you across it. A
 * needle turned by the yaw alone would be right at the four quarter-turns and
 * wrong by up to nine degrees everywhere between.
 *
 * North is **−Z**, which is what the generator means by it: latitude is read
 * off the plane's y axis with north at the top, and the client's plane is XZ.
 */

/** Where north is on screen, in radians clockwise from straight up. */
export function northOnScreen(yaw: number): number {
  // The world's −Z through the camera basis: across the screen by sin(yaw),
  // up it by cos(yaw), the latter squashed by the pitch.
  return Math.atan2(Math.sin(yaw), Math.cos(yaw) * Math.sin(PITCH));
}

export class Compass {
  private readonly element: HTMLElement;
  private readonly needle: SVGGElement;
  private drawn = Number.NaN;

  constructor(container: HTMLElement) {
    this.element = document.createElement('div');
    this.element.className = 'compass';
    this.element.title = 'północ';

    // Drawn rather than typed: an arrow made of text is a font away from being
    // a rectangle, and this one has to read at 44 pixels.
    this.element.innerHTML = `
      <svg viewBox="-24 -24 48 48" width="48" height="48" aria-hidden="true">
        <circle class="compass-face" cx="0" cy="0" r="21" />
        <g class="compass-needle">
          <path class="compass-north" d="M 0 -15 L 5 3 L 0 0 L -5 3 Z" />
          <path class="compass-south" d="M 0 15 L 5 3 L 0 0 L -5 3 Z" />
          <text class="compass-letter" x="0" y="-16">N</text>
        </g>
      </svg>
    `;

    this.needle = this.element.querySelector('.compass-needle') as SVGGElement;
    container.appendChild(this.element);
  }

  /**
   * Point it, given where the camera is looking now.
   *
   * Nothing is written unless the angle moved by more than a tenth of a degree:
   * this runs every frame, and setting an identical transform is a style
   * recalculation for nothing.
   */
  update(yaw: number): void {
    const degrees = (northOnScreen(yaw) * 180) / Math.PI;
    if (Math.abs(degrees - this.drawn) < 0.1) return;

    this.drawn = degrees;
    this.needle.setAttribute('transform', `rotate(${degrees.toFixed(1)})`);
  }

  dispose(): void {
    this.element.remove();
  }
}
